/**
 * Resolved theme values, keyed by the literal variable name. `null` records
 * "asked, and there is no usable colour there" so an unset variable is not
 * re-resolved on every call either.
 *
 * Keyed by the whole name on purpose: both namespaces reach this function.
 * Palette steps are `--color-brand-500` (a `@theme` var), while the semantic
 * roles #722 promoted are read by their `:root` name (`--surface-tile`) —
 * `@theme inline` emits no `--color-*` alias for those. A cache keyed on
 * anything narrower than the full name would miss half the call sites.
 */
const resolvedThemeColors = new Map<string, string | null>();

/**
 * Resolves a theme colour variable (from styles.css) to a concrete colour
 * string, for canvas-based libraries (lightweight-charts, amCharts) that
 * can't consume `var(--…)`. Keeps chart colours on the theme, so a rebrand
 * only retunes styles.css.
 *
 * The value is normalised through a 1×1 canvas to `rgba(r, g, b, a)`:
 * theme values may be written in any CSS colour syntax (e.g. `oklch(…)`),
 * which chart libraries don't parse.
 *
 * Returns `fallback` when the variable is unset, or when its value is not a
 * colour the browser can parse.
 *
 * Memoised per variable name, so the canvas is allocated once rather than once
 * per chart config. The *theme value* is cached, never the returned string:
 * `fallback` differs per call site, and caching the result would hand the
 * second caller the first one's fallback for the same variable.
 *
 * The cache makes the existing snapshot behaviour explicit rather than
 * introducing it — the value was already resolved once at chart-config time and
 * never revisited. A theme swapped at runtime (a `.dark` block, or a live
 * brand-preview toggle) must call `clearThemeColorCache()` and re-create the
 * charts.
 */
export function readThemeColor(variableName: `--${string}`, fallback: string): string {
  if (!resolvedThemeColors.has(variableName)) {
    resolvedThemeColors.set(variableName, resolveThemeColor(variableName));
  }

  return resolvedThemeColors.get(variableName) ?? fallback;
}

/**
 * Drops every memoised theme colour, so the next read re-resolves against the
 * current stylesheet. Call this after swapping the theme at runtime; charts
 * already built keep the old colour until they are re-created.
 */
export function clearThemeColorCache(): void {
  resolvedThemeColors.clear();
}

/** The variable's value as `rgba(…)`, or `null` if it is unset or unparsable. */
function resolveThemeColor(variableName: string): string | null {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  if (!value) {
    return null;
  }

  return normaliseColor(value);
}

function normaliseColor(color: string): string | null {
  const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (!context) {
    return null;
  }

  if (!isParsableColor(context, color)) {
    return null;
  }

  context.fillRect(0, 0, 1, 1);
  const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data;
  return `rgba(${red}, ${green}, ${blue}, ${Math.round((alpha / 255) * 1000) / 1000})`;
}

function isParsableColor(context: CanvasRenderingContext2D, color: string): boolean {
  context.fillStyle = '#000000';
  context.fillStyle = color;
  const overBlack = context.fillStyle;

  context.fillStyle = '#ffffff';
  context.fillStyle = color;

  return context.fillStyle === overBlack;
}
